import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { threadMessagesToChatLines, type ChatLine } from '../ai-chat-message.util';

export type ChatThreadSummary = {
    threadId: string;
    title?: string;
    updatedAt: string;
    contextUtilization: number;
};

type ThreadDetailResponse = {
    thread: {
        threadId: string;
        title?: string;
        messages: { role: string; content: { text?: string }[] }[];
    };
    contextUtilization: number;
};

@Injectable({ providedIn: 'root' })
export class AiChatThreadService {
    private readonly http = inject(HttpClient);

    readonly activeThreadId = signal<string | null>(null);
    readonly threads = signal<ChatThreadSummary[]>([]);
    readonly contextUtilization = signal(0);
    readonly loadedMessages = signal<ChatLine[]>([]);

    readonly isContextBlocked = computed(() => this.contextUtilization() >= 0.8);

    async refreshThreads(): Promise<void> {
        const response = await firstValueFrom(
            this.http.get<{ threads: ChatThreadSummary[] }>('/api/assistant/threads')
        );
        this.threads.set(response.threads);
    }

    async ensureActiveThread(): Promise<string> {
        const existing = this.activeThreadId();
        if (existing != null) {
            return existing;
        }
        return await this.createThread();
    }

    async switchThread(threadId: string): Promise<ChatLine[]> {
        this.activeThreadId.set(threadId);
        const response = await firstValueFrom(
            this.http.get<ThreadDetailResponse>(`/api/assistant/threads/${threadId}`)
        );
        this.contextUtilization.set(response.contextUtilization);
        const lines = threadMessagesToChatLines(response.thread.messages);
        this.loadedMessages.set(lines);
        return lines;
    }

    async createThread(): Promise<string> {
        const created = await firstValueFrom(
            this.http.post<{ threadId: string }>('/api/assistant/threads', {})
        );
        this.activeThreadId.set(created.threadId);
        this.loadedMessages.set([]);
        this.contextUtilization.set(0);
        await this.refreshThreads();
        return created.threadId;
    }

    async clearContext(): Promise<void> {
        const threadId = this.activeThreadId();
        if (threadId == null) {
            return;
        }
        await firstValueFrom(this.http.post(`/api/assistant/threads/${threadId}/clear`, {}));
        this.contextUtilization.set(0);
        this.loadedMessages.set([]);
    }

    async deleteThread(threadId: string): Promise<void> {
        await firstValueFrom(this.http.delete(`/api/assistant/threads/${threadId}`));
        if (this.activeThreadId() === threadId) {
            this.activeThreadId.set(null);
            this.loadedMessages.set([]);
            this.contextUtilization.set(0);
        }
        await this.refreshThreads();
    }

    async resetForIdentityChange(): Promise<void> {
        this.activeThreadId.set(null);
        this.loadedMessages.set([]);
        this.contextUtilization.set(0);
        await this.refreshThreads();
    }

    threadTitle(thread: ChatThreadSummary): string {
        return thread.title ?? `Thread ${thread.threadId.slice(0, 8)}`;
    }
}
